import { ref, readonly } from 'vue'
import { defineStore } from 'pinia'
import type { Patient } from '@/types'
import { authApi, patientApi } from '@/api'

export const usePatientStore = defineStore('patient', () => {
    // ===== STATE =====
    const patient = ref<Patient | null>(null)
    const isLoading = ref(false)
    const error = ref<string | null>(null)
    const isAuthenticated = ref(false)

    // ===== ACTIONS =====
    const login = async (email: string, password: string) => {
        isLoading.value = true

        try {
            const response = await authApi.patientLogin(email, password)

            const patientWithToken = {
                ...response.patient,
                token: response.token.access_token,
            }

            patient.value = patientWithToken
            isAuthenticated.value = true

            // Store only the token for persistence
            localStorage.setItem('patient_token', response.token.access_token)

            return response
        } catch (err) {
            isAuthenticated.value = false
            patient.value = null
            throw err
        } finally {
            isLoading.value = false
        }
    }

    const logout = () => {
        // needs to try and call logout endpoint in future
        patient.value = null
        isAuthenticated.value = false
        error.value = null

        // Clear stored token
        localStorage.removeItem('patient_token')
    }

    const updatePatient = (updatedData: Partial<Patient>) => {
        if (patient.value) {
            patient.value = { ...patient.value, ...updatedData }
        }
    }

    const ensurePatient = async () => {
        // If patient is already loaded, return it
        if (patient.value) {
            return patient.value
        }

        // Check if token exists in localStorage
        const storedToken = localStorage.getItem('patient_token')

        if (storedToken) {
            try {
                isLoading.value = true

                // Fetch patient data from API using the token
                // uses login endpoint for now until proper endpoint is available
                const response = await patientApi.getCurrentPatient()

                // Create patient with token (same as login flow)
                const patientWithToken = {
                    ...response,
                    token: storedToken,
                }

                patient.value = patientWithToken
                isAuthenticated.value = true
                return patient.value
            } catch (error) {
                // Token is invalid, clear it and require login
                //localStorage.removeItem('patient_token');
                isAuthenticated.value = false
                patient.value = null
                throw new Error('Invalid token, patient login required')
            } finally {
                isLoading.value = false
            }
        }

        // If no token exists, patient needs to login
        throw new Error('Patient login required')
    }

    // ===== RETURN PUBLIC API =====
    return {
        // State
        patient,
        isLoading: readonly(isLoading),
        error: readonly(error),
        isAuthenticated: readonly(isAuthenticated),

        // Actions
        login,
        logout,
        updatePatient,
        ensurePatient,
    }
})
